import type { QuoteReportResponse, QuoteRow } from '../interfaces/QuoteService';

/**
 * Authoritative wire shape returned by `/reports/consumer/quotes.json`.
 * Source of truth: `/Users/chris/Sera/consumer-portal/src/views/QuotesPage.vue`
 * (legacy Vue 2 view documenting the wire field names and columns Date, Name,
 * Description (summary), Quote # (id), Job # (job_id), Total).
 *
 * The row is the flat DataTables format with a dollar-string display for the
 * total money field. There is no SPB / progress billing augmentation and no
 * balance field (quotes have no balance dimension). The `name` and `summary`
 * fields are nullable on the wire; the adapter omits them entirely from the
 * domain row when the wire ships null or undefined so consumers can rely on
 * `in` checks (mirrors invoiceRow's `progressBilling` omission pattern).
 *
 * `summary` is an HTML string. The adapter preserves it verbatim; HTML entity
 * decoding is a view-layer concern (see QuotesPage.vue's `decodeHtml`).
 */
export interface QuoteWire {
  /** Quote id. The datatable typically ships a number but stringy ids are
   * tolerated as a defensive read (mirrors invoiceRow). */
  id: number | string;
  public_id?: string;
  /** Display date string from the wire, e.g. "May 20, 2026". Not ISO. */
  date?: string;
  /** Nullable on the wire. View layer applies the "Untitled Quote" fallback. */
  name?: string | null;
  /** Nullable HTML string. View layer decodes entities and applies the
   * "No description available" fallback. */
  summary?: string | null;
  job_id?: string;
  job_public_id?: string | null;
  /** Dollar string ("$340.00") or sentinel "--" when absent. */
  total?: string;
  /**
   * Decision status, titleized on the wire: "Pending" / "Denied" (D-02).
   * Currently dropped by the old adapter (Blocker A).
   */
  status?: string;
  /** Server-minted POST target for the proceed action (D-02, Blocker A). */
  proceed_url?: string;
  /** TC-708: real boolean (the datatable's sanitize_data keeps it unstringified). */
  customer_can_sign?: boolean;
}

/**
 * DataTables envelope shape. recordsTotal/recordsFiltered/draw are all
 * optional in this seam (defensive against test fixtures and API drift);
 * adaptQuoteReportResponse defaults each.
 */
export interface QuoteReportResponseWire {
  data: QuoteWire[];
  recordsTotal?: number;
  recordsFiltered?: number;
  draw?: number;
}

/**
 * Parse a wire dollar string ("$340.00", "$1,200.50") into integer cents.
 * Returns 0 for undefined, empty string, or the "--" sentinel so the domain
 * never carries NaN cents. Uses Math.round to avoid float drift from
 * parseFloat * 100. Mirrors invoiceRow's parseDollarsToCents.
 */
function parseDollarsToCents(raw: string | undefined): number {
  if (raw === undefined || raw === null) return 0;
  if (raw === '' || raw === '--') return 0;
  // WR-08: broaden the strip to all non-numeric/decimal/sign chars so trailing
  // unit strings ("$1,200.50 USD") don't slip through, and empty-after-strip
  // returns 0 explicitly rather than reaching parseFloat('').
  const stripped = raw.replace(/[^0-9.-]/g, '');
  if (!stripped) return 0;
  const n = parseFloat(stripped);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Coerce a wire id (number or string-numeric) to a number. Returns 0 on NaN
 * as a defensive fallback. Mirrors invoiceRow's normalizeId.
 */
function normalizeId(raw: number | string | undefined): number {
  if (typeof raw === 'number') return raw;
  if (raw === undefined || raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Map the titleized wire status onto the domain {@link QuoteRow.status} union
 * (Plan 09). Each known server value maps to its own status; only a genuine
 * "Pending" is the actionable 'pending'. Everything else — including unknown
 * values and a blank/absent status — degrades to the terminal 'closed' bucket
 * so a historical (accepted/expired/completed/...) quote can never masquerade
 * as an actionable pending quote (the quote-half of UAT Test 12). The badge's
 * pending-only count (D-03) therefore sees only true pending rows.
 */
function mapQuoteStatus(raw: string | undefined): QuoteRow['status'] {
  switch ((raw ?? '').toLowerCase()) {
    case 'pending':
      return 'pending';
    case 'denied':
      return 'denied';
    // TC-708 D-17: the Accepted tab's rows arrive as "Signed" (quote.status.titleize).
    case 'accepted':
    case 'signed':
      return 'accepted';
    case 'expired':
      return 'expired';
    default:
      return 'closed';
  }
}

/**
 * Map one wire quote row to the cents-based domain QuoteRow consumed by the
 * UI.
 *
 * The money field (`total`) is parsed from the wire's dollar-string display
 * into integer cents. A missing `total` defaults to 0. `id` is coerced to a
 * number; `job_id` and `date` are verbatim pass-through (the wire does not
 * ship an ISO date for this endpoint).
 *
 * The optional `name` and `summary` keys are omitted entirely (not set to
 * undefined) when the wire row ships null or undefined for them, so consumers
 * can use `'name' in row` checks. An empty-string `name` is preserved as `''`
 * (the "Untitled Quote" fallback lives in the view, not the adapter). The
 * same omit-vs-preserve rule applies to `summary`, and the HTML string is
 * preserved verbatim (the view layer decodes entities).
 */
export function adaptQuoteRow(wire: QuoteWire): QuoteRow {
  const row: QuoteRow = {
    id: normalizeId(wire.id),
    dateDisplay: wire.date ?? '',
    jobId: wire.job_id ?? '',
    totalCents: parseDollarsToCents(wire.total),
    // D-02 / Plan 09: lowercase the titleized wire status, then map each known
    // value EXPLICITLY. The .toLowerCase() is LOAD-BEARING — the wire ships
    // "Pending"/"Denied"/"Accepted"/"Expired"/... Only a genuine "Pending" is
    // actionable; every other value (including unknown/'') degrades to the
    // non-actionable 'closed' bucket so a historical quote can NEVER masquerade
    // as a pending one (the quote-half of UAT Test 12).
    status: mapQuoteStatus(wire.status),
    proceedUrl: wire.proceed_url ?? '',
    customerCanSign: wire.customer_can_sign === true,
  };
  if (wire.name !== null && wire.name !== undefined) {
    row.name = wire.name;
  }
  if (wire.summary !== null && wire.summary !== undefined) {
    row.summary = wire.summary;
  }
  return row;
}

/**
 * Map the full DataTables envelope. recordsTotal/recordsFiltered default to
 * data.length and draw defaults to 0 when omitted by the server (defensive
 * against test fixtures and API drift; mirrors invoiceRow envelope
 * tolerance).
 */
export function adaptQuoteReportResponse(
  wire: QuoteReportResponseWire,
): QuoteReportResponse {
  const data = (wire.data ?? []).map(adaptQuoteRow);
  return {
    data,
    recordsTotal: wire.recordsTotal ?? data.length,
    recordsFiltered: wire.recordsFiltered ?? data.length,
    draw: wire.draw ?? 0,
  };
}
